import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from '@/contexts/TranslationContext';

const LOGO_IMAGE = 'https://customer-assets.emergentagent.com/job_stray-care-1/artifacts/m4tj8pbh_1Smartpaw%20Post%20-%2034.PNG';

const goals = [
  {
    id: 1,
    type: 'Minimum goal',
    amount: '$6,500',
    description: '5 devices + 3-month ops + launch campaign',
    color: 'from-[#1e3a5f] to-[#2d5a8f]',
    textColor: 'text-white',
    bgColor: 'bg-[#1e3a5f]'
  },
  {
    id: 2,
    type: 'Stretch goal',
    amount: '$15,000',
    description: '15 feeders across Tbilisi',
    color: 'from-gray-50 to-gray-100',
    textColor: 'text-[#1e3a5f]',
    bgColor: 'bg-white',
    border: 'border-2 border-gray-200'
  },
  {
    id: 3,
    type: 'Scale goal',
    amount: '$27,500',
    description: '30 devices + full regional PR rollout',
    color: 'from-gray-50 to-gray-100',
    textColor: 'text-[#1e3a5f]',
    bgColor: 'bg-white',
    border: 'border-2 border-gray-200'
  },
  {
    id: 4,
    type: 'Dream goal',
    amount: '$45,000',
    description: '50 devices = Coverage of whole Georgia + Active PR and awareness Campaigns',
    color: 'from-gray-50 to-gray-100',
    textColor: 'text-[#1e3a5f]',
    bgColor: 'bg-white',
    border: 'border-2 border-gray-200'
  }
];

const BecomeSponsorPage = () => {
  const navigate = useNavigate();
  const { language, setLanguage, t } = useTranslation();
  const [currentAmount, setCurrentAmount] = useState(1750); // Current donation amount - matches hero banner
  const totalGoal = 45000; // Dream goal as the total
  const overallProgress = (currentAmount / totalGoal) * 100;

  const calculateProgress = (targetAmount) => {
    const numericTarget = parseInt(targetAmount.replace(/[$,]/g, ''));
    return Math.min((currentAmount / numericTarget) * 100, 100);
  };

  return (
    <div className="min-h-screen bg-[#FAFAF7]">
      {/* Header */}
      <header className="bg-white border-b border-gray-200 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-4 flex justify-between items-center">
          <button onClick={() => navigate('/')} className="flex items-center gap-2 hover:opacity-80 transition-opacity">
            <img src={LOGO_IMAGE} alt="SmartPaw Care" className="h-20 w-auto md:h-24" />
          </button>
          <div className="flex items-center gap-4">
            {/* Language Switcher */}
            <div className="flex items-center gap-2 bg-gray-100 rounded-full px-4 py-2">
              <button 
                onClick={() => setLanguage('en')}
                className={`font-['Manrope'] font-semibold text-sm transition-colors ${language === 'en' ? 'text-[#1A1C19]' : 'text-gray-400 hover:text-[#F4A460]'}`}
              >
                EN
              </button>
              <span className="text-gray-400">|</span>
              <button 
                onClick={() => setLanguage('ge')}
                className={`font-['Manrope'] font-semibold text-sm transition-colors ${language === 'ge' ? 'text-[#1A1C19]' : 'text-gray-400 hover:text-[#F4A460]'}`}
              >
                GE
              </button>
            </div>
            <button
              onClick={() => navigate('/')}
              className="text-[#5E635C] hover:text-[#1A1C19] font-['Manrope'] font-medium"
            >
              ← {language === 'ge' ? 'უკან' : 'Back to Home'}
            </button>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <div className="py-20 md:py-32">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <div className="inline-block px-6 py-2 bg-[#F4A460] rounded-full mb-6">
              <span className="font-['Manrope'] font-semibold text-white uppercase tracking-wider text-sm">
                Funding Ask
              </span>
            </div>
            <h1 className="font-['Outfit'] text-5xl md:text-7xl font-bold text-[#1A1C19] tracking-tight mb-6">
              Every Dollar Builds<br />Real Infrastructure
            </h1>
            <p className="font-['Manrope'] text-xl md:text-2xl text-[#5E635C] max-w-4xl mx-auto leading-relaxed">
              Operational costs are covered by QR payments. Donor capital goes exclusively to device production, installation, and awareness campaigns.
            </p>
          </motion.div>

          {/* Goals Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-12">
            {goals.map((goal, index) => (
              <motion.div
                key={goal.id}
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                className={`rounded-3xl p-8 ${goal.bgColor} ${goal.border || ''} shadow-[0_8px_30px_rgba(0,0,0,0.06)] hover:shadow-[0_8px_30px_rgba(0,0,0,0.12)] transition-all duration-300`}
              >
                <div className="mb-4">
                  <span className={`font-['Manrope'] text-sm font-semibold ${goal.textColor} ${goal.id === 1 ? 'text-[#F4A460]' : ''}`}>
                    {goal.type}
                  </span>
                </div>
                <div className={`font-['Outfit'] text-5xl md:text-6xl font-bold ${goal.textColor} mb-6`}>
                  {goal.amount}
                </div>
                <p className={`font-['Manrope'] ${goal.textColor} ${goal.id === 1 ? 'text-white/90' : 'text-[#5E635C]'} leading-relaxed min-h-[80px]`}>
                  {goal.description}
                </p>
              </motion.div>
            ))}
          </div>

          {/* Unified Progress Bar */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.5 }}
            className="mb-12 bg-white rounded-3xl p-8 shadow-[0_8px_30px_rgba(0,0,0,0.08)]"
          >
            <div className="flex justify-between items-center mb-4">
              <div>
                <h3 className="font-['Outfit'] text-2xl font-bold text-[#1A1C19] mb-1">
                  Overall Progress
                </h3>
                <p className="font-['Manrope'] text-[#5E635C]">
                  ${currentAmount.toLocaleString()} raised of ${totalGoal.toLocaleString()} goal
                </p>
              </div>
              <div className="text-right">
                <div className="font-['Outfit'] text-4xl font-bold text-[#F4A460]">
                  {Math.round(overallProgress)}%
                </div>
                <p className="font-['Manrope'] text-sm text-[#5E635C]">
                  Complete
                </p>
              </div>
            </div>
            <div className="w-full h-4 bg-gray-200 rounded-full overflow-hidden">
              <div
                className="h-full bg-gradient-to-r from-[#F4A460] to-[#4A90E2] rounded-full transition-all duration-500"
                style={{ width: `${overallProgress}%` }}
              />
            </div>
          </motion.div>

          {/* Footer Note */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.6, delay: 0.5 }}
            className="text-center"
          >
            <p className="font-['Manrope'] text-[#5E635C] italic">
              All donations above goal go toward additional devices and expanded campaigns.
            </p>
          </motion.div>

          {/* Contact Section */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.6 }}
            className="mt-20 bg-white rounded-3xl p-12 shadow-[0_8px_30px_rgba(0,0,0,0.08)]"
          >
            <h2 className="font-['Outfit'] text-4xl font-bold text-[#1A1C19] mb-8 text-center">
              Ready to Make an Impact?
            </h2>
            
            {/* Bank Transfer Details Section */}
            <div className="mb-12 bg-gradient-to-br from-[#FDF0E6] to-[#EBF3FC] rounded-2xl p-8">
              <h3 className="font-['Outfit'] font-semibold text-3xl text-[#1A1C19] mb-6 text-center">
                {t('sponsor.bankTitle')}
              </h3>
              <p className="font-['Manrope'] text-[#5E635C] mb-8 text-center">
                {t('sponsor.bankSubtitle')}
              </p>

              {/* Currency Accounts */}
              <div className="space-y-6 max-w-4xl mx-auto">
                {/* EUR Account */}
                <div className="bg-white rounded-xl p-6 shadow-sm border-2 border-[#4A90E2]">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-12 h-12 bg-[#4A90E2] rounded-full flex items-center justify-center">
                      <span className="text-white font-bold text-lg">€</span>
                    </div>
                    <h4 className="font-['Outfit'] font-bold text-xl text-[#1A1C19]">{t('sponsor.eurTransfer')}</h4>
                  </div>
                  <div className="space-y-2 font-['Manrope'] text-[#5E635C]">
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
                      <p className="font-semibold text-[#1A1C19]">{t('sponsor.bankName')}</p>
                      <p className="md:col-span-2">Bank of Georgia</p>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
                      <p className="font-semibold text-[#1A1C19]">{t('sponsor.accountHolder')}</p>
                      <p className="md:col-span-2">CleanPaw International LLC</p>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
                      <p className="font-semibold text-[#1A1C19]">{t('sponsor.iban')}</p>
                      <p className="md:col-span-2 font-mono">GE40BG0000000606083250</p>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
                      <p className="font-semibold text-[#1A1C19]">{t('sponsor.intermediary')}</p>
                      <p className="md:col-span-2">Commerzbank, Frankfurt, Germany</p>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
                      <p className="font-semibold text-[#1A1C19]">{t('sponsor.swiftIntermediary')}</p>
                      <p className="md:col-span-2 font-mono">COBADEFF</p>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
                      <p className="font-semibold text-[#1A1C19]">{t('sponsor.swiftBank')}</p>
                      <p className="md:col-span-2 font-mono">BAGAGE22</p>
                    </div>
                  </div>
                </div>

                {/* USD Account */}
                <div className="bg-white rounded-xl p-6 shadow-sm border-2 border-[#4A90E2]">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-12 h-12 bg-[#4A90E2] rounded-full flex items-center justify-center">
                      <span className="text-white font-bold text-lg">$</span>
                    </div>
                    <h4 className="font-['Outfit'] font-bold text-xl text-[#1A1C19]">{t('sponsor.usdTransfer')}</h4>
                  </div>
                  <div className="space-y-2 font-['Manrope'] text-[#5E635C]">
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
                      <p className="font-semibold text-[#1A1C19]">Bank Name:</p>
                      <p className="md:col-span-2">Bank of Georgia</p>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
                      <p className="font-semibold text-[#1A1C19]">Account Holder:</p>
                      <p className="md:col-span-2">CleanPaw International LLC</p>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
                      <p className="font-semibold text-[#1A1C19]">IBAN:</p>
                      <p className="md:col-span-2 font-mono">GE40BG0000000606083250</p>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
                      <p className="font-semibold text-[#1A1C19]">Intermediary Bank:</p>
                      <p className="md:col-span-2">Citibank N.A., New York, USA</p>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
                      <p className="font-semibold text-[#1A1C19]">SWIFT (Intermediary):</p>
                      <p className="md:col-span-2 font-mono">CITIUS33</p>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
                      <p className="font-semibold text-[#1A1C19]">SWIFT (Bank of Georgia):</p>
                      <p className="md:col-span-2 font-mono">BAGAGE22</p>
                    </div>
                  </div>
                </div>

                {/* GEL Account */}
                <div className="bg-white rounded-xl p-6 shadow-sm border-2 border-[#F4A460]">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-12 h-12 bg-[#F4A460] rounded-full flex items-center justify-center">
                      <span className="text-white font-bold text-lg">₾</span>
                    </div>
                    <h4 className="font-['Outfit'] font-bold text-xl text-[#1A1C19]">{t('sponsor.gelTransfer')}</h4>
                  </div>
                  <div className="space-y-2 font-['Manrope'] text-[#5E635C]">
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
                      <p className="font-semibold text-[#1A1C19]">Bank Name:</p>
                      <p className="md:col-span-2">Bank of Georgia (საქართველოს ბანკი)</p>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
                      <p className="font-semibold text-[#1A1C19]">Account Holder:</p>
                      <p className="md:col-span-2">CleanPaw International LLC (შპს ქლინფო ინტერნეიშენალ)</p>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
                      <p className="font-semibold text-[#1A1C19]">IBAN:</p>
                      <p className="md:col-span-2 font-mono">GE40BG0000000606083250</p>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
                      <p className="font-semibold text-[#1A1C19]">SWIFT:</p>
                      <p className="md:col-span-2 font-mono">BAGAGE22</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Transfer Instructions */}
              <div className="mt-8 bg-white rounded-xl p-6 max-w-4xl mx-auto">
                <h4 className="font-['Outfit'] font-semibold text-lg text-[#1A1C19] mb-4 flex items-center gap-2">
                  <svg className="w-6 h-6 text-[#F4A460]" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                  </svg>
                  {t('sponsor.instructionsTitle')}
                </h4>
                <ol className="space-y-3 font-['Manrope'] text-[#5E635C] list-decimal list-inside">
                  <li>{t('sponsor.instruction1')}</li>
                  <li>{t('sponsor.instruction2')}</li>
                  <li>{t('sponsor.instruction3')}</li>
                  <li className="flex items-start gap-2">
                    <span>4.</span>
                    <div className="flex-1">
                      <p className="mb-2">{t('sponsor.instruction4')}</p>
                      <input 
                        type="email" 
                        placeholder={t('sponsor.emailPlaceholder')}
                        className="w-full px-4 py-2 border-2 border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#F4A460] focus:border-transparent"
                      />
                    </div>
                  </li>
                </ol>
              </div>

              <p className="font-['Manrope'] text-sm text-center text-[#5E635C] mt-6 italic">
                {t('sponsor.taxNote')}
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              <div className="text-center md:text-left">
                <h3 className="font-['Outfit'] font-semibold text-xl text-[#1A1C19] mb-4">
                  Contact Information
                </h3>
                <div className="space-y-3 font-['Manrope'] text-[#5E635C]">
                  <p className="flex items-center gap-3 justify-center md:justify-start">
                    <svg className="w-5 h-5 text-[#F4A460]" fill="currentColor" viewBox="0 0 20 20">
                      <path d="M2.003 5.884L10 9.882l7.997-3.998A2 2 0 0016 4H4a2 2 0 00-1.997 1.884z" />
                      <path d="M18 8.118l-8 4-8-4V14a2 2 0 002 2h12a2 2 0 002-2V8.118z" />
                    </svg>
                    <span>info@smartpawcare.ge</span>
                  </p>
                  <p className="flex items-center gap-3 justify-center md:justify-start">
                    <svg className="w-5 h-5 text-[#F4A460]" fill="currentColor" viewBox="0 0 20 20">
                      <path d="M2 3a1 1 0 011-1h2.153a1 1 0 01.986.836l.74 4.435a1 1 0 01-.54 1.06l-1.548.773a11.037 11.037 0 006.105 6.105l.774-1.548a1 1 0 011.059-.54l4.435.74a1 1 0 01.836.986V17a1 1 0 01-1 1h-2C7.82 18 2 12.18 2 5V3z" />
                    </svg>
                    <span>+995 XXX XXX XXX</span>
                  </p>
                  <p className="flex items-center gap-3 justify-center md:justify-start">
                    <svg className="w-5 h-5 text-[#F4A460]" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M5.05 4.05a7 7 0 119.9 9.9L10 18.9l-4.95-4.95a7 7 0 010-9.9zM10 11a2 2 0 100-4 2 2 0 000 4z" clipRule="evenodd" />
                    </svg>
                    <span>Tbilisi, Georgia</span>
                  </p>
                </div>
              </div>
              <div className="text-center md:text-right">
                <h3 className="font-['Outfit'] font-semibold text-xl text-[#1A1C19] mb-4">
                  Need More Information?
                </h3>
                <p className="font-['Manrope'] text-[#5E635C] mb-4">
                  Have questions about sponsorship or want to discuss a larger contribution?
                </p>
                <button
                  onClick={() => window.location.href = 'mailto:info@smartpawcare.ge'}
                  className="px-6 py-3 bg-white border-2 border-[#F4A460] text-[#F4A460] rounded-full font-['Manrope'] font-semibold hover:bg-[#F4A460] hover:text-white transition-all duration-200"
                  data-testid="contact-us-button"
                >
                  Contact Us
                </button>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </div>
  );
};

export default BecomeSponsorPage;
